import { Calendar, Download, Mail } from 'lucide-react'
import { useState } from 'react'

import { Button } from '../components/ui/button'
import { Checkbox } from '../components/ui/checkbox'
import { Input } from '../components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs'

// Mock data
const mockSalesData = [
  {
    serie: 'TC-001',
    sucursal: 'Matriz',
    usuario: 'Juan Pérez',
    descuento: 50,
    importe: 1200,
    total: 1150,
  },
  {
    serie: 'EF-002',
    sucursal: 'Sucursal 1',
    usuario: 'María López',
    descuento: 0,
    importe: 850,
    total: 850,
  },
  {
    serie: 'TC-003',
    sucursal: 'Matriz',
    usuario: 'Carlos Ruiz',
    descuento: 100,
    importe: 2500,
    total: 2400,
  },
]

const mockProductSales = [
  {
    producto: 'Paracetamol 500mg',
    departamento: 'Analgésicos',
    categoria: 'OTC',
    cantidad: 150,
    precioConImpuestos: 45,
    importeConImpuestos: 6750,
  },
  {
    producto: 'Amoxicilina 500mg',
    departamento: 'Antibióticos',
    categoria: 'Receta',
    cantidad: 85,
    precioConImpuestos: 180,
    importeConImpuestos: 15300,
  },
]

const mockUtilidades = [
  {
    serie: 'TC-001',
    sucursal: 'Matriz',
    usuarioVendedor: 'Juan Pérez',
    descuento: 50,
    importe: 1200,
    costo: 800,
    utilidadPromedio: 33.33,
  },
  {
    serie: 'TC-002',
    sucursal: 'Sucursal 1',
    usuarioVendedor: 'María López',
    descuento: 0,
    importe: 850,
    costo: 600,
    utilidadPromedio: 29.41,
  },
]

const mockUserSales = [
  {
    usuario: 'Juan Pérez',
    noVentas: 45,
    noVentasCanceladas: 2,
    devoluciones: 3,
    devolucionesCanceladas: 0,
    promedio: 1250,
    descuentoPorcentaje: 5,
    importeTotal: 56250,
  },
  {
    usuario: 'María López',
    noVentas: 38,
    noVentasCanceladas: 1,
    devoluciones: 2,
    devolucionesCanceladas: 1,
    promedio: 980,
    descuentoPorcentaje: 3,
    importeTotal: 37240,
  },
]

const mockClientSales = [
  {
    cliente: 'Farmacia del Centro',
    noVentas: 12,
    ticketPromedio: 3500,
    descuentoPorcentaje: 8,
    importeConImpuestos: 42000,
  },
  {
    cliente: 'Consultorio Dr. Martínez',
    noVentas: 8,
    ticketPromedio: 2200,
    descuentoPorcentaje: 5,
    importeConImpuestos: 17600,
  },
]

export function Reports() {
  const [startDate, setStartDate] = useState('2026-01-01')
  const [endDate, setEndDate] = useState('2026-05-09')

  // Filtros comunes
  const [selectedBranch, setSelectedBranch] = useState('all')
  const [showWithTaxes, setShowWithTaxes] = useState(true)
  const [selectedStatus, setSelectedStatus] = useState('all')
  const [selectedCurrency, setSelectedCurrency] = useState('all')

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-slate-900">Reportes</h1>
        <div className="flex gap-2">
          <Button variant="outline">
            <Mail className="mr-2 h-4 w-4" />
            Enviar por Email
          </Button>
          <Button variant="outline">
            <Download className="mr-2 h-4 w-4" />
            Exportar a Excel
          </Button>
        </div>
      </div>

      <Tabs defaultValue="ventas" className="space-y-6">
        <TabsList>
          <TabsTrigger value="ventas">Ventas</TabsTrigger>
          <TabsTrigger value="devoluciones">Devoluciones</TabsTrigger>
          <TabsTrigger value="pedidos">Pedidos</TabsTrigger>
          <TabsTrigger value="cfdi">CFDI</TabsTrigger>
          <TabsTrigger value="compras">Compras</TabsTrigger>
          <TabsTrigger value="movimientos">Movimientos de Dinero</TabsTrigger>
          <TabsTrigger value="inventario">Inventario</TabsTrigger>
          <TabsTrigger value="cajas">Cajas de Cobro</TabsTrigger>
        </TabsList>

        {/* Sección Ventas */}
        <TabsContent value="ventas" className="space-y-6">
          <Tabs defaultValue="ventas-generales" className="space-y-4">
            <TabsList>
              <TabsTrigger value="ventas-generales">
                Ventas Generales
              </TabsTrigger>
              <TabsTrigger value="departamento-categoria">
                Por Departamento y Categoría
              </TabsTrigger>
              <TabsTrigger value="productos">Por Productos</TabsTrigger>
              <TabsTrigger value="utilidades">Utilidades</TabsTrigger>
              <TabsTrigger value="usuario">Por Usuario</TabsTrigger>
              <TabsTrigger value="vendedor">Por Vendedor</TabsTrigger>
              <TabsTrigger value="clientes">Por Clientes</TabsTrigger>
            </TabsList>

            {/* Ventas Generales */}
            <TabsContent value="ventas-generales" className="space-y-4">
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <h3 className="mb-4 font-semibold text-slate-900">Filtros</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Fecha Inicio
                    </label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Fecha Fin
                    </label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Sucursal
                    </label>
                    <Select
                      value={selectedBranch}
                      onValueChange={setSelectedBranch}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas</SelectItem>
                        <SelectItem value="matriz">Matriz</SelectItem>
                        <SelectItem value="suc1">Sucursal 1</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Estado
                    </label>
                    <Select
                      value={selectedStatus}
                      onValueChange={setSelectedStatus}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="vigente">Vigente</SelectItem>
                        <SelectItem value="cancelado">Cancelado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Moneda
                    </label>
                    <Select
                      value={selectedCurrency}
                      onValueChange={setSelectedCurrency}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas</SelectItem>
                        <SelectItem value="mxn">MXN</SelectItem>
                        <SelectItem value="usd">USD</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Tipo de Pago
                    </label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="contado">Al contado</SelectItem>
                        <SelectItem value="credito">Crédito</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="mt-6 flex items-center space-x-2">
                    <Checkbox
                      id="withTaxes"
                      checked={showWithTaxes}
                      onCheckedChange={setShowWithTaxes}
                    />
                    <label
                      htmlFor="withTaxes"
                      className="text-sm font-medium text-slate-700"
                    >
                      Ver cantidades con impuestos
                    </label>
                  </div>
                </div>
                <div className="mt-4">
                  <Button>
                    <Calendar className="mr-2 h-4 w-4" />
                    Aplicar Filtros
                  </Button>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                <table className="w-full">
                  <thead className="border-b border-slate-200 bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Serie/Folio
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Sucursal
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Usuario/Vendedor
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Descuento
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Importe
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {mockSalesData.map((sale) => (
                      <tr key={sale.serie} className="hover:bg-slate-50">
                        <td className="px-6 py-4 font-medium text-slate-900">
                          {sale.serie}
                        </td>
                        <td className="px-6 py-4 text-slate-700">
                          {sale.sucursal}
                        </td>
                        <td className="px-6 py-4 text-slate-700">
                          {sale.usuario}
                        </td>
                        <td className="px-6 py-4 text-slate-700">
                          ${sale.descuento.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 text-slate-700">
                          ${sale.importe.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 font-semibold text-green-600">
                          ${sale.total.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="border-t border-slate-200 bg-slate-50">
                    <tr>
                      <td
                        colSpan={3}
                        className="px-6 py-4 font-semibold text-slate-900"
                      >
                        Totales
                      </td>
                      <td className="px-6 py-4 font-semibold text-slate-900">
                        $150
                      </td>
                      <td className="px-6 py-4 font-semibold text-slate-900">
                        $4,550
                      </td>
                      <td className="px-6 py-4 font-semibold text-green-600">
                        $4,400
                      </td>
                    </tr>
                    <tr>
                      <td
                        colSpan={6}
                        className="px-6 py-3 text-sm text-slate-600"
                      >
                        Total de registros: 3
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </TabsContent>

            {/* Ventas por Departamento y Categoría */}
            <TabsContent value="departamento-categoria" className="space-y-4">
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <h3 className="mb-4 font-semibold text-slate-900">Filtros</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Fecha Inicio
                    </label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Fecha Fin
                    </label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Departamento
                    </label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="analgesicos">Analgésicos</SelectItem>
                        <SelectItem value="antibioticos">
                          Antibióticos
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Categoría
                    </label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Todas" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas</SelectItem>
                        <SelectItem value="otc">OTC</SelectItem>
                        <SelectItem value="receta">Receta</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="mt-6 flex items-center space-x-2">
                    <Checkbox id="devoluciones" />
                    <label
                      htmlFor="devoluciones"
                      className="text-sm font-medium text-slate-700"
                    >
                      Incluir Devoluciones
                    </label>
                  </div>
                </div>
                <div className="mt-4">
                  <Button>
                    <Calendar className="mr-2 h-4 w-4" />
                    Aplicar Filtros
                  </Button>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                <table className="w-full">
                  <thead className="border-b border-slate-200 bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Serie/Folio
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Sucursal
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Usuario/Vendedor
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Descuento
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Importe
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {mockSalesData.map((sale) => (
                      <tr key={sale.serie} className="hover:bg-slate-50">
                        <td className="px-6 py-4 font-medium text-slate-900">
                          {sale.serie}
                        </td>
                        <td className="px-6 py-4 text-slate-700">
                          {sale.sucursal}
                        </td>
                        <td className="px-6 py-4 text-slate-700">
                          {sale.usuario}
                        </td>
                        <td className="px-6 py-4 text-slate-700">
                          ${sale.descuento.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 text-slate-700">
                          ${sale.importe.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 font-semibold text-green-600">
                          ${sale.total.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="border-t border-slate-200 bg-slate-50">
                    <tr>
                      <td
                        colSpan={6}
                        className="px-6 py-4 font-semibold text-slate-900"
                      >
                        Suma de totales de campos | Suma de totales con
                        impuestos
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </TabsContent>

            {/* Ventas por Productos */}
            <TabsContent value="productos" className="space-y-4">
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <h3 className="mb-4 font-semibold text-slate-900">Filtros</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Fecha Inicio
                    </label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Fecha Fin
                    </label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Producto
                    </label>
                    <Input placeholder="Buscar producto..." />
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox id="agrupar" />
                    <label
                      htmlFor="agrupar"
                      className="text-sm font-medium text-slate-700"
                    >
                      Agrupar y promediar
                    </label>
                  </div>
                </div>
                <div className="mt-4">
                  <Button>
                    <Calendar className="mr-2 h-4 w-4" />
                    Aplicar Filtros
                  </Button>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                <table className="w-full">
                  <thead className="border-b border-slate-200 bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Producto
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Departamento
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Categoría
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Cantidad
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Precio con Impuestos
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Importe con Impuestos
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {mockProductSales.map((product) => (
                      <tr key={product.producto} className="hover:bg-slate-50">
                        <td className="px-6 py-4 font-medium text-slate-900">
                          {product.producto}
                        </td>
                        <td className="px-6 py-4 text-slate-700">
                          {product.departamento}
                        </td>
                        <td className="px-6 py-4 text-slate-700">
                          {product.categoria}
                        </td>
                        <td className="px-6 py-4 text-slate-700">
                          {product.cantidad}
                        </td>
                        <td className="px-6 py-4 text-slate-700">
                          ${product.precioConImpuestos.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 font-semibold text-green-600">
                          ${product.importeConImpuestos.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="border-t border-slate-200 bg-slate-50">
                    <tr>
                      <td
                        colSpan={6}
                        className="px-6 py-4 font-semibold text-slate-900"
                      >
                        Suma de totales de campos | Suma de totales con
                        impuestos
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </TabsContent>

            {/* Utilidades */}
            <TabsContent value="utilidades" className="space-y-4">
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <h3 className="mb-4 font-semibold text-slate-900">Filtros</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Fecha Inicio
                    </label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Fecha Fin
                    </label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Documento
                    </label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="ventas">Ventas</SelectItem>
                        <SelectItem value="abonos">Abonos</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-4">
                  <Button>
                    <Calendar className="mr-2 h-4 w-4" />
                    Aplicar Filtros
                  </Button>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                <table className="w-full">
                  <thead className="border-b border-slate-200 bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Serie/Folio
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Sucursal
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Usuario Vendedor
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Descuento
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Importe
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Costo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Utilidad Promedio %
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {mockUtilidades.map((util) => (
                      <tr key={util.serie} className="hover:bg-slate-50">
                        <td className="px-6 py-4 font-medium text-slate-900">
                          {util.serie}
                        </td>
                        <td className="px-6 py-4 text-slate-700">
                          {util.sucursal}
                        </td>
                        <td className="px-6 py-4 text-slate-700">
                          {util.usuarioVendedor}
                        </td>
                        <td className="px-6 py-4 text-slate-700">
                          ${util.descuento.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 text-slate-700">
                          ${util.importe.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 text-slate-700">
                          ${util.costo.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 font-semibold text-green-600">
                          {util.utilidadPromedio.toFixed(2)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="border-t border-slate-200 bg-slate-50">
                    <tr>
                      <td
                        colSpan={7}
                        className="px-6 py-4 font-semibold text-slate-900"
                      >
                        Suma de totales de campos | Suma de totales con
                        impuestos | Total registros: 2
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </TabsContent>

            {/* Ventas por Usuario */}
            <TabsContent value="usuario" className="space-y-4">
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <h3 className="mb-4 font-semibold text-slate-900">Filtros</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Fecha Inicio
                    </label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Fecha Fin
                    </label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Ordenar Por
                    </label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Ventas" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ventas">Ventas</SelectItem>
                        <SelectItem value="importe">Importe Total</SelectItem>
                        <SelectItem value="noVentas">No. ventas</SelectItem>
                        <SelectItem value="canceladas">
                          No. ventas canceladas
                        </SelectItem>
                        <SelectItem value="descuento">Descuento</SelectItem>
                        <SelectItem value="promedio">Promedio</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox id="verDevoluciones" />
                    <label
                      htmlFor="verDevoluciones"
                      className="text-sm font-medium text-slate-700"
                    >
                      Ver Devoluciones de ventas
                    </label>
                  </div>
                </div>
                <div className="mt-4">
                  <Button>
                    <Calendar className="mr-2 h-4 w-4" />
                    Aplicar Filtros
                  </Button>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                <table className="w-full">
                  <thead className="border-b border-slate-200 bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Usuario
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        No. Ventas
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        No. Ventas Canceladas
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Devoluciones
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Dev. Canceladas
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Promedio
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Descuento %
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Importe Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {mockUserSales.map((user) => (
                      <tr key={user.usuario} className="hover:bg-slate-50">
                        <td className="px-6 py-4 font-medium text-slate-900">
                          {user.usuario}
                        </td>
                        <td className="px-6 py-4 text-slate-700">
                          {user.noVentas}
                        </td>
                        <td className="px-6 py-4 text-slate-700">
                          {user.noVentasCanceladas}
                        </td>
                        <td className="px-6 py-4 text-slate-700">
                          {user.devoluciones}
                        </td>
                        <td className="px-6 py-4 text-slate-700">
                          {user.devolucionesCanceladas}
                        </td>
                        <td className="px-6 py-4 text-slate-700">
                          ${user.promedio.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 text-slate-700">
                          {user.descuentoPorcentaje}%
                        </td>
                        <td className="px-6 py-4 font-semibold text-green-600">
                          ${user.importeTotal.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="border-t border-slate-200 bg-slate-50">
                    <tr>
                      <td
                        colSpan={8}
                        className="px-6 py-4 font-semibold text-slate-900"
                      >
                        Suma de totales de campos | Suma de totales con
                        impuestos
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </TabsContent>

            {/* Ventas por Vendedor */}
            <TabsContent value="vendedor" className="space-y-4">
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <h3 className="mb-4 font-semibold text-slate-900">Filtros</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Fecha Inicio
                    </label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Fecha Fin
                    </label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox id="verDevolucionesVendedor" />
                    <label
                      htmlFor="verDevolucionesVendedor"
                      className="text-sm font-medium text-slate-700"
                    >
                      Ver Devoluciones de ventas
                    </label>
                  </div>
                </div>
                <div className="mt-4">
                  <Button>
                    <Calendar className="mr-2 h-4 w-4" />
                    Aplicar Filtros
                  </Button>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                <table className="w-full">
                  <thead className="border-b border-slate-200 bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Vendedor
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        No. Ventas
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        No. Ventas Canceladas
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Devoluciones
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Dev. Canceladas
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Promedio
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Descuento %
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Importe Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {mockUserSales.map((user) => (
                      <tr key={user.usuario} className="hover:bg-slate-50">
                        <td className="px-6 py-4 font-medium text-slate-900">
                          {user.usuario}
                        </td>
                        <td className="px-6 py-4 text-slate-700">
                          {user.noVentas}
                        </td>
                        <td className="px-6 py-4 text-slate-700">
                          {user.noVentasCanceladas}
                        </td>
                        <td className="px-6 py-4 text-slate-700">
                          {user.devoluciones}
                        </td>
                        <td className="px-6 py-4 text-slate-700">
                          {user.devolucionesCanceladas}
                        </td>
                        <td className="px-6 py-4 text-slate-700">
                          ${user.promedio.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 text-slate-700">
                          {user.descuentoPorcentaje}%
                        </td>
                        <td className="px-6 py-4 font-semibold text-green-600">
                          ${user.importeTotal.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="border-t border-slate-200 bg-slate-50">
                    <tr>
                      <td
                        colSpan={8}
                        className="px-6 py-4 font-semibold text-slate-900"
                      >
                        Suma de totales de campos | Suma de totales con
                        impuestos
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </TabsContent>

            {/* Ventas por Clientes */}
            <TabsContent value="clientes" className="space-y-4">
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <h3 className="mb-4 font-semibold text-slate-900">Filtros</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Fecha Inicio
                    </label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Fecha Fin
                    </label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Cliente
                    </label>
                    <Input placeholder="Buscar cliente..." />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Descuento
                    </label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="si">Sí</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-4">
                  <Button>
                    <Calendar className="mr-2 h-4 w-4" />
                    Aplicar Filtros
                  </Button>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                <table className="w-full">
                  <thead className="border-b border-slate-200 bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Cliente
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        No. Ventas
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Ticket Promedio
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Descuento %
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Importe con Impuestos
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {mockClientSales.map((client) => (
                      <tr key={client.cliente} className="hover:bg-slate-50">
                        <td className="px-6 py-4 font-medium text-slate-900">
                          {client.cliente}
                        </td>
                        <td className="px-6 py-4 text-slate-700">
                          {client.noVentas}
                        </td>
                        <td className="px-6 py-4 text-slate-700">
                          ${client.ticketPromedio.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 text-slate-700">
                          {client.descuentoPorcentaje}%
                        </td>
                        <td className="px-6 py-4 font-semibold text-green-600">
                          ${client.importeConImpuestos.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="border-t border-slate-200 bg-slate-50">
                    <tr>
                      <td
                        colSpan={5}
                        className="px-6 py-4 font-semibold text-slate-900"
                      >
                        Suma de totales de campos
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* Sección Devoluciones */}
        <TabsContent value="devoluciones" className="space-y-4">
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <h3 className="mb-4 font-semibold text-slate-900">Filtros</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Fecha Inicio
                </label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Fecha Fin
                </label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Sucursal
                </label>
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    <SelectItem value="matriz">Matriz</SelectItem>
                    <SelectItem value="suc1">Sucursal 1</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Estado
                </label>
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="vigente">Vigente</SelectItem>
                    <SelectItem value="cancelado">Cancelado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Tipo de Pago
                </label>
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="contado">Al contado</SelectItem>
                    <SelectItem value="credito">Crédito</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4">
              <Button>
                <Calendar className="mr-2 h-4 w-4" />
                Aplicar Filtros
              </Button>
            </div>
          </div>

          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <table className="w-full">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Serie-Folio
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Sucursal
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Usuario
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Descuento
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Importe
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                <tr className="hover:bg-slate-50">
                  <td className="px-6 py-4 font-medium text-slate-900">
                    DEV-001
                  </td>
                  <td className="px-6 py-4 text-slate-700">Matriz</td>
                  <td className="px-6 py-4 text-slate-700">Juan Pérez</td>
                  <td className="px-6 py-4 text-slate-700">$20</td>
                  <td className="px-6 py-4 font-semibold text-red-600">$480</td>
                </tr>
              </tbody>
              <tfoot className="border-t border-slate-200 bg-slate-50">
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-4 font-semibold text-slate-900"
                  >
                    Suma de totales de campos | Suma de totales con impuestos |
                    Total registros: 1
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </TabsContent>

        {/* Sección Pedidos */}
        <TabsContent value="pedidos" className="space-y-6">
          <Tabs defaultValue="pedidos-general" className="space-y-4">
            <TabsList>
              <TabsTrigger value="pedidos-general">Pedidos</TabsTrigger>
              <TabsTrigger value="por-repartidor">Por Repartidor</TabsTrigger>
            </TabsList>

            <TabsContent value="pedidos-general" className="space-y-4">
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <h3 className="mb-4 font-semibold text-slate-900">Filtros</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Fecha Inicio
                    </label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Fecha Fin
                    </label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Estado
                    </label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="pendiente">Pendiente</SelectItem>
                        <SelectItem value="entregado">Entregado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-4">
                  <Button>
                    <Calendar className="mr-2 h-4 w-4" />
                    Aplicar Filtros
                  </Button>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                <table className="w-full">
                  <thead className="border-b border-slate-200 bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Serie-Folio
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Sucursal
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Usuario
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Descuento
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Importe
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    <tr className="hover:bg-slate-50">
                      <td className="px-6 py-4 font-medium text-slate-900">
                        PED-001
                      </td>
                      <td className="px-6 py-4 text-slate-700">Matriz</td>
                      <td className="px-6 py-4 text-slate-700">María López</td>
                      <td className="px-6 py-4 text-slate-700">$50</td>
                      <td className="px-6 py-4 font-semibold text-green-600">
                        $1,450
                      </td>
                    </tr>
                  </tbody>
                  <tfoot className="border-t border-slate-200 bg-slate-50">
                    <tr>
                      <td
                        colSpan={5}
                        className="px-6 py-4 font-semibold text-slate-900"
                      >
                        Suma de totales de campos | Suma de totales con
                        impuestos | Total registros: 1
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </TabsContent>

            <TabsContent value="por-repartidor" className="space-y-4">
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <h3 className="mb-4 font-semibold text-slate-900">Filtros</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Fecha Inicio
                    </label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Fecha Fin
                    </label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Ordenar Por
                    </label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Pedidos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pedidos">Pedidos</SelectItem>
                        <SelectItem value="importe">Importe total</SelectItem>
                        <SelectItem value="noPedidos">No. pedidos</SelectItem>
                        <SelectItem value="cancelados">
                          No. pedidos cancelados
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-4">
                  <Button>
                    <Calendar className="mr-2 h-4 w-4" />
                    Aplicar Filtros
                  </Button>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                <table className="w-full">
                  <thead className="border-b border-slate-200 bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Repartidor
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        No. Pedidos
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        No. Pedidos Cancelados
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Promedio
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Descuento %
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    <tr className="hover:bg-slate-50">
                      <td className="px-6 py-4 font-medium text-slate-900">
                        Roberto García
                      </td>
                      <td className="px-6 py-4 text-slate-700">28</td>
                      <td className="px-6 py-4 text-slate-700">2</td>
                      <td className="px-6 py-4 text-slate-700">$850</td>
                      <td className="px-6 py-4 text-slate-700">4%</td>
                    </tr>
                  </tbody>
                  <tfoot className="border-t border-slate-200 bg-slate-50">
                    <tr>
                      <td
                        colSpan={5}
                        className="px-6 py-4 font-semibold text-slate-900"
                      >
                        Suma de totales de campos | Suma de totales con
                        impuestos
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* Sección CFDI */}
        <TabsContent value="cfdi" className="space-y-6">
          <Tabs defaultValue="facturas" className="space-y-4">
            <TabsList>
              <TabsTrigger value="facturas">Facturas (Impuestos)</TabsTrigger>
              <TabsTrigger value="notas-credito">Notas de Crédito</TabsTrigger>
            </TabsList>

            <TabsContent value="facturas" className="space-y-4">
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <h3 className="mb-4 font-semibold text-slate-900">Filtros</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Fecha Inicio
                    </label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Fecha Fin
                    </label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Estado
                    </label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="vigente">Vigente</SelectItem>
                        <SelectItem value="cancelado">Cancelado</SelectItem>
                        <SelectItem value="pendiente">Pendiente</SelectItem>
                        <SelectItem value="fallido">Fallido</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Moneda
                    </label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Todas" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas</SelectItem>
                        <SelectItem value="mxn">MXN</SelectItem>
                        <SelectItem value="usd">USD</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-4 flex gap-2">
                  <Button>
                    <Calendar className="mr-2 h-4 w-4" />
                    Aplicar Filtros
                  </Button>
                  <Button variant="outline">
                    <Download className="mr-2 h-4 w-4" />
                    Exportar a Excel
                  </Button>
                  <Button variant="outline">
                    <Mail className="mr-2 h-4 w-4" />
                    Enviar por Email
                  </Button>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                <table className="w-full">
                  <thead className="border-b border-slate-200 bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Folio Fiscal
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Cliente
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Fecha
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Estado
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    <tr className="hover:bg-slate-50">
                      <td className="px-6 py-4 font-medium text-slate-900">
                        A1B2C3D4
                      </td>
                      <td className="px-6 py-4 text-slate-700">
                        Farmacia del Centro
                      </td>
                      <td className="px-6 py-4 text-slate-700">2026-05-08</td>
                      <td className="px-6 py-4">
                        <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-700">
                          Vigente
                        </span>
                      </td>
                      <td className="px-6 py-4 font-semibold text-green-600">
                        $3,500
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </TabsContent>

            <TabsContent value="notas-credito" className="space-y-4">
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <h3 className="mb-4 font-semibold text-slate-900">Filtros</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Fecha Inicio
                    </label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Fecha Fin
                    </label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Estado
                    </label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="vigente">Vigente</SelectItem>
                        <SelectItem value="cancelado">Cancelado</SelectItem>
                        <SelectItem value="pendiente">Pendiente</SelectItem>
                        <SelectItem value="fallido">Fallido</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-4 flex gap-2">
                  <Button>
                    <Calendar className="mr-2 h-4 w-4" />
                    Aplicar Filtros
                  </Button>
                  <Button variant="outline">
                    <Download className="mr-2 h-4 w-4" />
                    Exportar a Excel
                  </Button>
                  <Button variant="outline">
                    <Mail className="mr-2 h-4 w-4" />
                    Enviar por Email
                  </Button>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                <table className="w-full">
                  <thead className="border-b border-slate-200 bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Folio Fiscal
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Cliente
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Fecha
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Estado
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    <tr className="py-8 text-center">
                      <td colSpan={5} className="px-6 py-8 text-slate-500">
                        No hay notas de crédito registradas
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* Sección Compras */}
        <TabsContent value="compras" className="space-y-6">
          <Tabs defaultValue="compras-generales" className="space-y-4">
            <TabsList>
              <TabsTrigger value="compras-generales">
                Compras Generales
              </TabsTrigger>
              <TabsTrigger value="por-producto">Por Producto</TabsTrigger>
              <TabsTrigger value="por-proveedor">Por Proveedor</TabsTrigger>
            </TabsList>

            <TabsContent value="compras-generales" className="space-y-4">
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <h3 className="mb-4 font-semibold text-slate-900">Filtros</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Fecha Inicio
                    </label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Fecha Fin
                    </label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Proveedor
                    </label>
                    <Input placeholder="Buscar proveedor..." />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Forma de Pago
                    </label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Todas" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas</SelectItem>
                        <SelectItem value="efectivo">Efectivo</SelectItem>
                        <SelectItem value="transferencia">
                          Transferencia
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-4">
                  <Button>
                    <Calendar className="mr-2 h-4 w-4" />
                    Aplicar Filtros
                  </Button>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                <table className="w-full">
                  <thead className="border-b border-slate-200 bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Serie-Folio
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Sucursal
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Usuario
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Descuento
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Importe
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    <tr className="hover:bg-slate-50">
                      <td className="px-6 py-4 font-medium text-slate-900">
                        COM-001
                      </td>
                      <td className="px-6 py-4 text-slate-700">Matriz</td>
                      <td className="px-6 py-4 text-slate-700">Admin</td>
                      <td className="px-6 py-4 text-slate-700">$100</td>
                      <td className="px-6 py-4 font-semibold text-primary">
                        $8,900
                      </td>
                    </tr>
                  </tbody>
                  <tfoot className="border-t border-slate-200 bg-slate-50">
                    <tr>
                      <td
                        colSpan={5}
                        className="px-6 py-4 font-semibold text-slate-900"
                      >
                        Suma de totales de campos | Suma de totales con
                        impuestos | Total registros: 1
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </TabsContent>

            <TabsContent value="por-producto" className="space-y-4">
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <h3 className="mb-4 font-semibold text-slate-900">Filtros</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Fecha Inicio
                    </label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Fecha Fin
                    </label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Producto
                    </label>
                    <Input placeholder="Buscar producto..." />
                  </div>
                </div>
                <div className="mt-4">
                  <Button>
                    <Calendar className="mr-2 h-4 w-4" />
                    Aplicar Filtros
                  </Button>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                <table className="w-full">
                  <thead className="border-b border-slate-200 bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Producto
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Departamento
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Categoría
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Cantidad
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Precio con Impuestos
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {mockProductSales.map((product) => (
                      <tr key={product.producto} className="hover:bg-slate-50">
                        <td className="px-6 py-4 font-medium text-slate-900">
                          {product.producto}
                        </td>
                        <td className="px-6 py-4 text-slate-700">
                          {product.departamento}
                        </td>
                        <td className="px-6 py-4 text-slate-700">
                          {product.categoria}
                        </td>
                        <td className="px-6 py-4 text-slate-700">
                          {product.cantidad}
                        </td>
                        <td className="px-6 py-4 font-semibold text-primary">
                          ${product.precioConImpuestos.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="border-t border-slate-200 bg-slate-50">
                    <tr>
                      <td
                        colSpan={5}
                        className="px-6 py-4 font-semibold text-slate-900"
                      >
                        Importe total y con impuestos | Suma de totales de
                        campos
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </TabsContent>

            <TabsContent value="por-proveedor" className="space-y-4">
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <h3 className="mb-4 font-semibold text-slate-900">Filtros</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Fecha Inicio
                    </label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Fecha Fin
                    </label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Proveedor
                    </label>
                    <Input placeholder="Buscar proveedor..." />
                  </div>
                </div>
                <div className="mt-4">
                  <Button>
                    <Calendar className="mr-2 h-4 w-4" />
                    Aplicar Filtros
                  </Button>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                <table className="w-full">
                  <thead className="border-b border-slate-200 bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Proveedor
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        No. Compra
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Ticket Promedio
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Descuento %
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    <tr className="hover:bg-slate-50">
                      <td className="px-6 py-4 font-medium text-slate-900">
                        Distribuidora Farmacéutica SA
                      </td>
                      <td className="px-6 py-4 text-slate-700">15</td>
                      <td className="px-6 py-4 text-slate-700">$12,500</td>
                      <td className="px-6 py-4 text-slate-700">8%</td>
                    </tr>
                  </tbody>
                  <tfoot className="border-t border-slate-200 bg-slate-50">
                    <tr>
                      <td
                        colSpan={4}
                        className="px-6 py-4 font-semibold text-slate-900"
                      >
                        Suma de totales de campos | Suma de totales con
                        impuestos
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* Sección Movimientos de Dinero */}
        <TabsContent value="movimientos" className="space-y-4">
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <h3 className="mb-4 font-semibold text-slate-900">Filtros</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Fecha Inicio
                </label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Fecha Fin
                </label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Tipo
                </label>
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="entrada">Entrada</SelectItem>
                    <SelectItem value="salida">Salida</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Movimiento
                </label>
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="ventas">Ventas</SelectItem>
                    <SelectItem value="compras">Compras</SelectItem>
                    <SelectItem value="caja">Movimientos de caja</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Forma de Pago
                </label>
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    <SelectItem value="efectivo">Efectivo</SelectItem>
                    <SelectItem value="tarjeta">Tarjeta</SelectItem>
                    <SelectItem value="transferencia">Transferencia</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4">
              <Button>
                <Calendar className="mr-2 h-4 w-4" />
                Aplicar Filtros
              </Button>
            </div>
          </div>

          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <table className="w-full">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Movimiento
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Usuario
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Caja de Cobro / Sucursal
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Forma de Pago
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Entrada
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                    Salidas
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                <tr className="hover:bg-slate-50">
                  <td className="px-6 py-4 font-medium text-slate-900">
                    Venta TC-001
                  </td>
                  <td className="px-6 py-4 text-slate-700">Juan Pérez</td>
                  <td className="px-6 py-4 text-slate-700">Caja 1 / Matriz</td>
                  <td className="px-6 py-4 text-slate-700">Efectivo</td>
                  <td className="px-6 py-4 font-semibold text-green-600">
                    $1,150
                  </td>
                  <td className="px-6 py-4 text-slate-400">-</td>
                </tr>
                <tr className="hover:bg-slate-50">
                  <td className="px-6 py-4 font-medium text-slate-900">
                    Retiro de Caja
                  </td>
                  <td className="px-6 py-4 text-slate-700">Admin</td>
                  <td className="px-6 py-4 text-slate-700">Caja 1 / Matriz</td>
                  <td className="px-6 py-4 text-slate-700">Efectivo</td>
                  <td className="px-6 py-4 text-slate-400">-</td>
                  <td className="px-6 py-4 font-semibold text-red-600">
                    $5,000
                  </td>
                </tr>
              </tbody>
              <tfoot className="border-t border-slate-200 bg-slate-50">
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-4 font-semibold text-slate-900"
                  >
                    Suma de totales | Total registros: 2
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </TabsContent>

        {/* Sección Inventario */}
        <TabsContent value="inventario" className="space-y-6">
          <Tabs defaultValue="existencias" className="space-y-4">
            <TabsList>
              <TabsTrigger value="existencias">Existencias</TabsTrigger>
              <TabsTrigger value="minimos-maximos">
                Mínimos y Máximos
              </TabsTrigger>
              <TabsTrigger value="cofepris">Cofepris</TabsTrigger>
              <TabsTrigger value="lotes">Lotes</TabsTrigger>
              <TabsTrigger value="kardex">Kardex de Producto</TabsTrigger>
            </TabsList>

            <TabsContent value="existencias" className="space-y-4">
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <h3 className="mb-4 font-semibold text-slate-900">Filtros</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Producto
                    </label>
                    <Input placeholder="Buscar producto..." />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Existencia
                    </label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Todo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todo</SelectItem>
                        <SelectItem value="sin">Sin existencia</SelectItem>
                        <SelectItem value="negativa">Negativa</SelectItem>
                        <SelectItem value="con">Con existencia</SelectItem>
                        <SelectItem value="minimo">Menor al mínimo</SelectItem>
                        <SelectItem value="excedido">Excedido</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="mt-6 flex items-center space-x-2">
                    <Checkbox id="mostrarUtilidades" />
                    <label
                      htmlFor="mostrarUtilidades"
                      className="text-sm font-medium text-slate-700"
                    >
                      Mostrar utilidades
                    </label>
                  </div>
                </div>
                <div className="mt-4">
                  <Button>
                    <Calendar className="mr-2 h-4 w-4" />
                    Aplicar Filtros
                  </Button>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                <table className="w-full">
                  <thead className="border-b border-slate-200 bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Producto
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Costo Promedio
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Existencias
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Unidad
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Costo Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {mockProductSales.map((product) => (
                      <tr key={product.producto} className="hover:bg-slate-50">
                        <td className="px-6 py-4 font-medium text-slate-900">
                          {product.producto}
                        </td>
                        <td className="px-6 py-4 text-slate-700">$35</td>
                        <td className="px-6 py-4 text-slate-700">
                          {product.cantidad}
                        </td>
                        <td className="px-6 py-4 text-slate-700">Pieza</td>
                        <td className="px-6 py-4 font-semibold text-primary">
                          $5,250
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="border-t border-slate-200 bg-slate-50">
                    <tr>
                      <td
                        colSpan={5}
                        className="px-6 py-4 font-semibold text-slate-900"
                      >
                        Total de costos: $15,450
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </TabsContent>

            <TabsContent value="minimos-maximos" className="space-y-4">
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <h3 className="mb-4 font-semibold text-slate-900">Filtros</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Sucursal
                    </label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Todas" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas</SelectItem>
                        <SelectItem value="matriz">Matriz</SelectItem>
                        <SelectItem value="suc1">Sucursal 1</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Departamento
                    </label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="analgesicos">Analgésicos</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-4 flex gap-2">
                  <Button>
                    <Calendar className="mr-2 h-4 w-4" />
                    Aplicar Filtros
                  </Button>
                  <Button variant="outline">
                    <Download className="mr-2 h-4 w-4" />
                    Exportar a Excel
                  </Button>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="cofepris" className="space-y-4">
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <h3 className="mb-4 font-semibold text-slate-900">Filtros</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Fecha Inicio
                    </label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Fecha Fin
                    </label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                </div>
                <div className="mt-4 flex gap-2">
                  <Button>
                    <Calendar className="mr-2 h-4 w-4" />
                    Aplicar Filtros
                  </Button>
                  <Button variant="outline">
                    <Download className="mr-2 h-4 w-4" />
                    Exportar a Excel
                  </Button>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="lotes" className="space-y-4">
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <h3 className="mb-4 font-semibold text-slate-900">Filtros</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Sucursal
                    </label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Todas" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas</SelectItem>
                        <SelectItem value="matriz">Matriz</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Estado
                    </label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="activos">Activos</SelectItem>
                        <SelectItem value="expirados">Expirados</SelectItem>
                        <SelectItem value="proximos">
                          Próximos a caducar
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-4 flex gap-2">
                  <Button>
                    <Calendar className="mr-2 h-4 w-4" />
                    Aplicar Filtros
                  </Button>
                  <Button variant="outline">
                    <Download className="mr-2 h-4 w-4" />
                    Exportar a Excel
                  </Button>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="kardex" className="space-y-4">
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <h3 className="mb-4 font-semibold text-slate-900">Filtros</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Fecha Inicio
                    </label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Fecha Fin
                    </label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Producto
                    </label>
                    <Input placeholder="Buscar producto..." />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Documentos
                    </label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="ventas">Ventas</SelectItem>
                        <SelectItem value="compras">Compras</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-4 flex gap-2">
                  <Button>
                    <Calendar className="mr-2 h-4 w-4" />
                    Aplicar Filtros
                  </Button>
                  <Button variant="outline">
                    <Download className="mr-2 h-4 w-4" />
                    Exportar a Excel
                  </Button>
                  <Button variant="outline">
                    <Mail className="mr-2 h-4 w-4" />
                    Enviar por Email
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* Sección Cajas de Cobro */}
        <TabsContent value="cajas" className="space-y-6">
          <Tabs defaultValue="saldos" className="space-y-4">
            <TabsList>
              <TabsTrigger value="saldos">Saldos de Caja</TabsTrigger>
              <TabsTrigger value="cortes">Cortes de Caja</TabsTrigger>
            </TabsList>

            <TabsContent value="saldos" className="space-y-4">
              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                <table className="w-full">
                  <thead className="border-b border-slate-200 bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Caja
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Sucursal
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Saldo Actual
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Movimientos Hoy
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    <tr className="hover:bg-slate-50">
                      <td className="px-6 py-4 font-medium text-slate-900">
                        Caja 1
                      </td>
                      <td className="px-6 py-4 text-slate-700">Matriz</td>
                      <td className="px-6 py-4 font-semibold text-green-600">
                        $15,450
                      </td>
                      <td className="px-6 py-4 text-slate-700">23</td>
                    </tr>
                    <tr className="hover:bg-slate-50">
                      <td className="px-6 py-4 font-medium text-slate-900">
                        Caja 2
                      </td>
                      <td className="px-6 py-4 text-slate-700">Sucursal 1</td>
                      <td className="px-6 py-4 font-semibold text-green-600">
                        $8,920
                      </td>
                      <td className="px-6 py-4 text-slate-700">15</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </TabsContent>

            <TabsContent value="cortes" className="space-y-4">
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <h3 className="mb-4 font-semibold text-slate-900">Filtros</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Fecha Inicio
                    </label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Fecha Fin
                    </label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Saldo
                    </label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="diferencia">
                          Con diferencia
                        </SelectItem>
                        <SelectItem value="negativa">
                          Diferencia negativa
                        </SelectItem>
                        <SelectItem value="positiva">
                          Diferencia positiva
                        </SelectItem>
                        <SelectItem value="sin">Sin diferencia</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Usuario
                    </label>
                    <Input placeholder="Buscar usuario..." />
                  </div>
                </div>
                <div className="mt-4">
                  <Button>
                    <Calendar className="mr-2 h-4 w-4" />
                    Aplicar Filtros
                  </Button>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                <table className="w-full">
                  <thead className="border-b border-slate-200 bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Caja de Cobro
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Folio
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Moneda
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Usuario
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase">
                        Resultado
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    <tr className="hover:bg-slate-50">
                      <td className="px-6 py-4 font-medium text-slate-900">
                        Caja 1
                      </td>
                      <td className="px-6 py-4 text-slate-700">CORTE-001</td>
                      <td className="px-6 py-4 text-slate-700">MXN</td>
                      <td className="px-6 py-4 text-slate-700">Juan Pérez</td>
                      <td className="px-6 py-4">
                        <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-700">
                          Sin diferencia
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </TabsContent>
          </Tabs>
        </TabsContent>
      </Tabs>
    </div>
  )
}
